import { skipToken } from "@reduxjs/toolkit/query";
import {
  IntegrationProvider,
  type Integration,
} from "@repo/app-types/entities/integration";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import type { FC } from "react";
import { Navigate, Outlet, useOutletContext } from "react-router";
import { useRecordingS3IntakeFailedImportsCountQuery } from "../../../api/entities/recordingS3IntakeIntegration.js";
import { appPaths } from "../../../app/appPaths.js";
import {
  PageBody,
  TabbedPageLayout,
} from "../../../components/layout/pageLayout.js";
import {
  RouteTabs,
  type RouteTabItem,
} from "../../../components/layout/routeTabs.js";
import { useAppSelector } from "../../../hooks/hook.js";
import { selectRecordingS3IntakeIntegration } from "../../integrations/integrationsSlice.js";
import { IntegrationLogo } from "./integrationLogo.js";

const TITLE = "Recording S3 intake";

/**
 * What the Settings and Failed imports tabs share: the synthetic S3-intake
 * integration from the boot payload. Handed down through the router `Outlet` so
 * the tabs read it without re-selecting.
 */
export interface RecordingS3IntakeDetailOutletContext {
  integration: Integration;
}

export function useRecordingS3IntakeDetailOutletContext(): RecordingS3IntakeDetailOutletContext {
  return useOutletContext<RecordingS3IntakeDetailOutletContext>();
}

/**
 * The "Recording S3 intake" detail page: a wider, tabbed frame
 * (like Genesys) hosting a **Settings** tab (bucket, example key, and — for
 * RingCX customers — the agent email mapping) and a **Failed imports** tab (the
 * company's own failed `recording_intake` rows).
 *
 * The synthetic integration comes from the boot payload and only exists for
 * intake customers, so a company that isn't one is redirected back to the
 * integrations overview (e.g. a stale deep link).
 */
export const RecordingS3IntakeIntegrationDetailSection: FC = () => {
  const integration = useAppSelector(selectRecordingS3IntakeIntegration);

  // Only meaningful once we know the company is an intake customer; skip the
  // request otherwise so a non-customer deep link doesn't fire it before the
  // redirect below.
  const failedCount = useRecordingS3IntakeFailedImportsCountQuery(
    integration ? undefined : skipToken,
  );

  if (!integration) {
    return <Navigate to={appPaths.settingsIntegrations.build()} replace />;
  }

  const count = failedCount.data?.count ?? 0;
  const tabs: RouteTabItem[] = [
    { label: "Settings", to: "." },
    {
      label:
        count > 0
          ? `Failed imports (${count.toLocaleString()})`
          : "Failed imports",
      to: "failed-imports",
    },
  ];

  const context: RecordingS3IntakeDetailOutletContext = { integration };

  return (
    <TabbedPageLayout
      header={
        <PageHeader
          width={PageWidth.Wide}
          title={
            <span className="flex min-w-0 items-center gap-2">
              <IntegrationLogo
                provider={IntegrationProvider.RECORDING_S3_INTAKE}
              />
              <span className="truncate">{TITLE}</span>
            </span>
          }
          tabs={
            <RouteTabs ariaLabel="Recording S3 intake sections" tabs={tabs} />
          }
        />
      }
    >
      <PageBody width={PageWidth.Wide}>
        <Outlet context={context} />
      </PageBody>
    </TabbedPageLayout>
  );
};
